/**
 * FamilyBiz - spec 10's review queue (FR-7.2).
 *
 * `?queue=true` is the default view: what needs a decision. That is a submission
 * awaiting review (a first submission, or an edit to a live listing, which stays
 * `approved` while it waits) plus an owner's disable/enable request, and they need
 * different buttons - which is why `requested_action` is read rather than inferred
 * from the status word alone.
 *
 * The status vocabulary here is the FULL eight values, and it is worth knowing how
 * they divide (this is the rule spec 10's DECISIONS entry records):
 *
 *   draft                     the owner is still writing it; nothing to review
 *   pending                   submitted, awaiting an admin decision
 *   approved                  live in the member directory
 *   disable_requested         live, and the owner has asked to take it down
 *   enable_requested          disabled, and the owner has asked to put it back
 *   rejected                  refused; the owner can edit and resubmit
 *   suspended                 taken down by an admin
 *   disabled                  taken down at the owner's request
 *
 * `reject` and `request-changes` both require remarks, and `resolve-request` does
 * when declining - FR-7.2.2's "a reason the member can act on". The dialogs enforce
 * it so the round trip is not wasted; the server refuses it regardless.
 */

import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { DataTable, PrimaryCell, type Column } from "../components/DataTable";
import { ListingActions } from "../components/ListingActions";
import {
  Badge,
  Button,
  Card,
  ErrorNotice,
  EmptyState,
  Field,
  Input,
  LoadingRows,
  Page,
  Pagination,
  Select,
  SuccessNotice,
  statusTone,
} from "../components/primitives";
import { fetchListings, type ListingAdmin } from "../lib/api/familybiz";
import type { ListingStatus, Page as PageType } from "../lib/api/types";
import { useQuery } from "../lib/hooks";
import { formatDate, humanizeStatus, relativeDay } from "../lib/format";

const PAGE_SIZE = 50;
const STATUSES: ListingStatus[] = [
  "draft",
  "pending",
  "approved",
  "rejected",
  "suspended",
  "disabled",
  "disable_requested",
  "enable_requested",
];

export function FamilyBizScreen() {
  const [params, setParams] = useSearchParams();

  const queue = params.get("queue") === "true";
  const status = (params.get("status") ?? "") as ListingStatus | "";
  const ownerQ = params.get("owner_q") ?? "";
  const q = params.get("q") ?? "";
  const offset = Number(params.get("offset") ?? 0) || 0;

  const [term, setTerm] = useState(q);
  const [ownerTerm, setOwnerTerm] = useState(ownerQ);
  const [saved, setSaved] = useState<string | null>(null);

  function apply(changes: Record<string, string | number | boolean | null>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === "" || value === false) next.delete(key);
      else next.set(key, String(value));
    }
    if (!("offset" in changes)) next.delete("offset");
    setParams(next, { replace: true });
  }

  const query = useQuery<PageType<ListingAdmin>>(
    () =>
      fetchListings({
        queue: queue || undefined,
        status: status || undefined,
        q: q || undefined,
        owner_q: ownerQ || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    [queue, status, q, ownerQ, offset],
  );
  const { data, error, loading, refreshing, reload } = query;

  const columns: Array<Column<ListingAdmin>> = [
    {
      key: "listing",
      header: "Listing",
      render: (row) => {
        // The live name is the published version's; a listing that has never been
        // approved has none, so the pending or draft version names it instead.
        const version = row.published ?? row.pending ?? row.draft;
        return (
          <PrimaryCell
            title={
              <Link to={`/family-biz/${row.id}`} style={{ textDecoration: "underline" }}>
                {version?.business_name ?? "Unnamed listing"}
              </Link>
            }
            detail={version?.category_name ?? "Uncategorised"}
          />
        );
      },
    },
    {
      key: "owner",
      header: "Owner",
      render: (row) => (
        <PrimaryCell
          title={row.owner_name ?? "Removed member"}
          detail={row.owner_mobile_number ?? undefined}
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <>
          <Badge tone={statusTone(row.status)}>{humanizeStatus(row.status)}</Badge>
          {row.requested_action ? (
            <>
              {" "}
              <Badge tone="pending">
                asks to {row.requested_action}
              </Badge>
            </>
          ) : null}
        </>
      ),
    },
    {
      key: "submitted",
      header: "Submitted",
      render: (row) =>
        row.submitted_at ? (
          <PrimaryCell
            title={formatDate(row.submitted_at)}
            detail={relativeDay(row.submitted_at)}
          />
        ) : (
          <span className="text-meta">Never</span>
        ),
    },
    {
      key: "remarks",
      header: "Last note",
      render: (row) =>
        row.latest_remarks ? (
          <span className="text-small">{row.latest_remarks}</span>
        ) : (
          <span className="text-meta">-</span>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <ListingActions
          listing={row}
          onChanged={(message) => {
            setSaved(message);
            reload();
          }}
        />
      ),
    },
  ];

  return (
    <Page>
      {saved ? <SuccessNotice message={saved} onDismiss={() => setSaved(null)} /> : null}

      <Card>
        <div className="filters">
          <div className="row row--tight" style={{ paddingBottom: 8 }}>
            <Button
              variant={queue ? "primary" : "ghost"}
              onClick={() => apply({ queue: queue ? null : true, status: null })}
            >
              {queue ? "Showing the queue" : "Show the queue"}
            </Button>
            {queue ? (
              <Button onClick={() => apply({ queue: null })}>Show everything</Button>
            ) : null}
          </div>

          <div style={{ width: 240 }}>
            <Field label="Listing name">
              {(id) => (
                <Input
                  id={id}
                  value={term}
                  onChange={(event) => setTerm(event.target.value)}
                  onBlur={() => apply({ q: term.trim() })}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") apply({ q: term.trim() });
                  }}
                  placeholder="Press Enter to search"
                  maxLength={160}
                />
              )}
            </Field>
          </div>

          <div style={{ width: 240 }}>
            <Field label="Owner name or mobile">
              {(id) => (
                <Input
                  id={id}
                  value={ownerTerm}
                  onChange={(event) => setOwnerTerm(event.target.value)}
                  onBlur={() => apply({ owner_q: ownerTerm.trim() })}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") apply({ owner_q: ownerTerm.trim() });
                  }}
                  placeholder="Press Enter to search"
                  maxLength={160}
                />
              )}
            </Field>
          </div>

          <div style={{ width: 180 }}>
            <Field label="Status">
              {(id) => (
                <Select
                  id={id}
                  value={status}
                  // Picking a status leaves the queue: the two are different
                  // questions, and the API ignores `status` while `queue` is set.
                  onChange={(event) => apply({ status: event.target.value, queue: null })}
                  disabled={queue}
                >
                  <option value="">All</option>
                  {STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {humanizeStatus(value)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <div className="row row--tight">
            <Button
              onClick={() => {
                setTerm("");
                setOwnerTerm("");
                apply({ q: null, owner_q: null, status: null });
              }}
            >
              Clear
            </Button>
            <Button onClick={reload} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        </div>
      </Card>

      {error ? <ErrorNotice error={error} onRetry={reload} /> : null}

      <Card flush>
        {loading && !data ? (
          <LoadingRows rows={6} columns={6} />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title={queue ? "Nothing waiting for review." : "No listings match these filters."}
            body={
              queue
                ? "Submissions and owner requests appear here as soon as members send them."
                : "Members submit listings from the FamilyBiz tab of the app."
            }
          />
        ) : (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(row) => row.id} />
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              noun="listings"
              onChange={(next) => apply({ offset: next })}
            />
          </>
        )}
      </Card>

    </Page>
  );
}
